import { create } from "zustand"

import type { Dialogue, DialogueEntry, GameEvent } from "@/types"

export type EventPhase =
    | "idle"
    | "dialogue"
    | "narrative"
    | "choice"
    | "result"
    | "postDialog"

export function resolveDialogues(entries: DialogueEntry[]): Dialogue[] {
    return entries.map((entry) =>
        Array.isArray(entry)
            ? entry[Math.floor(Math.random() * entry.length)]
            : entry,
    )
}

interface EventState {
    currentEvent: GameEvent | null
    dialogueIndex: number
    isTyping: boolean
    selectedChoiceId: string | null
    resolvedPostDialogues: Dialogue[]
    resolvedDialogueCache: Record<string, Dialogue[]>
    phase: EventPhase

    triggerEvent: (event: GameEvent) => void
    advanceDialogue: () => void
    skipTyping: () => void
    selectChoice: (choiceId: string) => void
    closeEvent: () => void
    setIsTyping: (typing: boolean) => void
    setPhase: (phase: EventPhase) => void
}

export const useEventStore = create<EventState>((set, get) => ({
    currentEvent: null,
    dialogueIndex: 0,
    isTyping: false,
    selectedChoiceId: null,
    resolvedPostDialogues: [],
    resolvedDialogueCache: {},
    phase: "idle",

    triggerEvent: (event) => {
        if (event.type === "dialog") {
            set({
                currentEvent: event,
                dialogueIndex: 0,
                isTyping: true,
                selectedChoiceId: null,
                phase: "dialogue",
            })
        } else {
            set({
                currentEvent: event,
                dialogueIndex: 0,
                isTyping: true,
                selectedChoiceId: null,
                phase: "narrative",
            })
        }
    },

    advanceDialogue: () => {
        const { currentEvent, dialogueIndex, phase } = get()
        if (!currentEvent) return

        if (currentEvent.type === "dialog") {
            if (dialogueIndex < currentEvent.dialogues.length - 1) {
                set({ dialogueIndex: dialogueIndex + 1, isTyping: true })
            } else {
                get().closeEvent()
            }
            return
        }

        if (phase === "narrative") {
            if (currentEvent.choices.length > 0) {
                set({ phase: "choice", isTyping: false })
            } else {
                get().closeEvent()
            }
        } else if (phase === "result") {
            const { resolvedPostDialogues } = get()
            if (resolvedPostDialogues.length > 0) {
                set({ phase: "postDialog", dialogueIndex: 0, isTyping: true })
            } else {
                get().closeEvent()
            }
        } else if (phase === "postDialog") {
            const { resolvedPostDialogues } = get()
            if (dialogueIndex < resolvedPostDialogues.length - 1) {
                set({ dialogueIndex: dialogueIndex + 1, isTyping: true })
            } else {
                get().closeEvent()
            }
        }
    },

    skipTyping: () => {
        set({ isTyping: false })
    },

    selectChoice: (choiceId) => {
        const { currentEvent, resolvedDialogueCache } = get()
        const choice = currentEvent?.choices.find((c) => c.id === choiceId)
        const resolved = choice ? resolveDialogues(choice.nextDialogues) : []
        set({
            selectedChoiceId: choiceId,
            phase: "result",
            dialogueIndex: 0,
            isTyping: true,
            resolvedPostDialogues: resolved,
            resolvedDialogueCache: currentEvent
                ? { ...resolvedDialogueCache, [currentEvent.id]: resolved }
                : resolvedDialogueCache,
        })
    },

    closeEvent: () => {
        set({ phase: "idle" })
        setTimeout(() => {
            if (get().phase === "idle") {
                set({
                    currentEvent: null,
                    dialogueIndex: 0,
                    isTyping: false,
                    selectedChoiceId: null,
                    resolvedPostDialogues: [],
                })
            }
        }, 350)
    },

    setIsTyping: (typing) => {
        set({ isTyping: typing })
    },

    setPhase: (phase) => {
        set({ phase })
    },
}))

export function getCachedDialogues(eventId: string): Dialogue[] | undefined {
    const cache = useEventStore.getState().resolvedDialogueCache[eventId]
    return cache?.length ? cache : undefined
}
